/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild, signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LikeService } from '../services/like.service';
import { CommunityFeedComponent } from './feed/community-feed.component';

// Angular replacement for the AngularJS `community` page, routed by UTEST_DESIGN_REFRESH_2026.
@Component({
  selector: 'ut-community-page',
  templateUrl: './community-page.component.html',
  styleUrls: ['./community-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunityPageComponent implements OnInit, OnDestroy {
  @Input() user: any;

  @ViewChild(CommunityFeedComponent) feed: CommunityFeedComponent;

  testerAppUrl = signal('');
  featured = signal<any[] | undefined>(undefined);
  trending = signal<any[] | undefined>(undefined);

  // "Back to top" appears when scrolling back up through the infinite feed.
  showBackToTop = signal(false);
  private scroller: Element | null = null;
  private lastScrollTop = 0;

  constructor(
    @Inject('UserService') private UserService: any,
    @Inject('ConfigService') private ConfigService: any,
    @Inject('LightTesterErrorService') private LightTesterErrorService: any,
    @Inject('HomeFeedService') private HomeFeedService: any,
    private likeService: LikeService,
    private destroyRef: DestroyRef,
    private host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    if (!this.user.has_viewed_community_page) {
      this.UserService.setViewedCommunityPage();
    }
    this.HomeFeedService.pinned().then((res) => this.featured.set(res.data));
    this.HomeFeedService.trending().then((res) => this.trending.set(res.data));
    this.ConfigService.config().then((res) => {
      this.testerAppUrl.set(`${res.data.profile_url}/account/my-profile/`);
    });
    this.likeService.changed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((likeable) => this.syncTrending(likeable));
    // The page scrolls inside the app's main container, not the window, so listen in the capture phase.
    document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    (this.scroller as HTMLElement | null)?.style.removeProperty('scroll-padding-bottom');
  }

  private onScroll = (event: Event): void => {
    const target = event.target === document ? document.scrollingElement : event.target as Element;
    if (!target?.contains(this.host.nativeElement)) return;
    if (this.scroller !== target) {
      this.scroller = target;
      // Keeps elements focused by Tab clear of the fixed Back to top button.
      (target as HTMLElement).style.setProperty('scroll-padding-bottom', '8rem');
    }
    const top = target.scrollTop;
    if (top < 600) {
      this.showBackToTop.set(false);
    } else if (top < this.lastScrollTop - 4) {
      this.showBackToTop.set(true);
    } else if (top > this.lastScrollTop + 4) {
      this.showBackToTop.set(false);
    }
    this.lastScrollTop = top;
  };

  backToTop(): void {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scroller?.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    this.showBackToTop.set(false);
    // Move keyboard focus to the top too, so the next Tab starts from there.
    this.host.nativeElement.querySelector<HTMLElement>('.community-page__title')?.focus({ preventScroll: true });
  }

  // Trending items are separate objects from feed items, so copy like and comment counts across.
  syncTrending(changed: any): void {
    const items = this.trending();
    const match = items?.find((item) => item.id === changed.id && item.type === changed.type);
    if (!match) return;
    match.likes = { ...match.likes, count: changed.likes.count };
    if (changed.comments) {
      match.comments = { ...match.comments, count: changed.comments.count };
    } else {
      match.post_count = changed.post_count;
    }
    this.trending.set([...items]);
  }

  checkLightTester(event: Event): void {
    if (this.LightTesterErrorService.show()) {
      event.preventDefault();
    }
  }
}
